import type {
  AssistantProfile,
  AssistantStats,
  AuditEvent,
  DetectionHit,
  ProjectBudget,
  ProjectPause,
  ProjectSpend,
  ProvenanceRecord,
  SkillEvalCase,
  SkillEvalResult,
  StopSwitch,
  EnvironmentPoolStats,
  FactoryAgentSummary,
  FactoryMetrics,
  GatewayStats,
  GraphNodeRecord,
  LlmGatewayStats,
  ConnectionDef,
  PermissionLevel,
  PolicySpec,
  ResolvedStep,
  RunIntent,
  SkillManifest,
  SkillUsage,
  ShareLink,
  SlackChannelBinding,
  SlackChannelPurpose,
  SlackPreference,
  SlackReviewRoute,
  WarmEnvironment,
  WorkflowConfig,
} from '@reef/schema';

/*
 * The wire contract, shared with the Worker that produces it. Re-exported
 * because every screen imports its types from this module and the contract's
 * location is not their business.
 */
export type {
  AnalyticsSummary,
  ConfiguratorField,
  ConnectionResourcesResponse,
  ConnectionSummary,
  ConnectorManifestSummary,
  DeploymentReadiness,
  FactoryDetail,
  FactoryReposResponse,
  FactoryStageDetail,
  FactoryStageSummary,
  FactoryStepAccess,
  FactorySummary,
  WorkItemRow,
  WorkItemsResponse,
  Goal,
  GroupSummary,
  InboxItem,
  InboxItemAction,
  InboxItemKind,
  InboxResponse,
  LinearTeamSummary,
  MissingConnection,
  OutstandingShareLink,
  PlatformSummary,
  PolicyEditResponse,
  PolicyListResponse,
  PolicySaveResponse,
  ResolvedGrant,
  RunCeilings,
  RunListResponse,
  RunListStatus,
  RunRow,
  RunSummary,
  ReviewItem,
  SearchHit,
  SearchResponse,
  SearchRoute,
  SecretSummary,
  SlackChannelSummary,
  SlackIdentity,
  SlackSettings,
  StagedActionSummary,
  StepRunSummary,
  TokenDay,
  TokenWindow,
  WebhookEndpointSummary,
  WorkflowSharing,
  WorkflowSummary,
} from '@reef/api-contract';

/*
 * The transport, and the three refusals it turns into types. Re-exported so
 * every screen keeps importing them from here — what they need is the client,
 * not knowledge of where its fetch lives.
 */
export {
  ConnectionsRequiredError,
  DeploymentAtCapacityError,
  StaleEditorError,
  announceSignedIn,
  reloadWhenSignedIn,
  request,
  startOktaSignIn,
} from './http.ts';
import { request } from './http.ts';
import type {
  AnalyticsSummary,
  ConfiguratorField,
  ConnectionResourcesResponse,
  ConnectionSummary,
  ConnectorManifestSummary,
  DeploymentReadiness,
  FactoryDetail,
  FactoryReposResponse,
  FactoryStageDetail,
  FactoryStageSummary,
  FactoryStepAccess,
  FactorySummary,
  WorkItemRow,
  WorkItemsResponse,
  Goal,
  GroupSummary,
  InboxResponse,
  LinearTeamSummary,
  MissingConnection,
  OutstandingShareLink,
  PlatformSummary,
  PolicyEditResponse,
  PolicyListResponse,
  PolicySaveResponse,
  ResolvedGrant,
  RunCeilings,
  RunListResponse,
  RunListStatus,
  RunSummary,
  ReviewItem,
  SearchResponse,
  SecretSummary,
  SlackChannelSummary,
  SlackIdentity,
  SlackSettings,
  StagedActionSummary,
  StepRunSummary,
  TokenDay,
  TokenWindow,
  WebhookEndpointSummary,
  WorkflowSharing,
  WorkflowSummary,
} from '@reef/api-contract';

/** What to call a connection on screen: its name, falling back to its id. */
export function connectionName(connections: readonly ConnectionSummary[], id: string): string {
  return connections.find((entry) => entry.id === id)?.name ?? id;
}

/**
 * Typed client for the reef control plane: one method per endpoint the studio
 * calls, over the shared transport in `http.ts`.
 *
 * A screen reaching an endpoint this does not wrap should import `request`
 * rather than write its own `fetch` — five of them once did, and every copy was
 * missing something the shared one had.
 */
export const api = {
  me: () =>
    request<{ userId: string; email: string; name: string | null; isAdmin: boolean }>('/me'),

  /** What this deployment is missing: booleans for everyone, secret names for admins. */
  deploymentReadiness: () => request<DeploymentReadiness>('/deployment/readiness'),

  listWorkflows: () => request<{ workflows: WorkflowSummary[] }>('/workflows'),

  getWorkflow: (id: string) =>
    request<{ workflow: WorkflowConfig; revision: number; yourLevel: PermissionLevel }>(
      `/workflows/${encodeURIComponent(id)}`,
    ),

  /**
   * `expectedRevision` is the revision this editor loaded, not the one it is
   * writing. Sending it is what turns "two people had this open" from silent
   * data loss into a 409 the builder can explain.
   */
  saveWorkflow: (
    id: string,
    workflow: WorkflowConfig,
    expectedRevision: number | null,
    reason: string,
  ) =>
    request<{ workflow: WorkflowConfig; revision: number }>(
      `/workflows/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          workflow,
          expectedRevision: expectedRevision ?? undefined,
          reason,
        }),
      },
    ),

  duplicateWorkflow: (id: string, name?: string) =>
    request<{ workflow: WorkflowConfig }>(`/workflows/${encodeURIComponent(id)}/duplicate`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  deleteWorkflow: (id: string) =>
    request<{ deleted: string; commit: string | null }>(`/workflows/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  updateSharing: (id: string, sharing: WorkflowConfig['sharing']) =>
    request<{ sharing: WorkflowConfig['sharing'] }>(
      `/workflows/${encodeURIComponent(id)}/sharing`,
      { method: 'PUT', body: JSON.stringify({ sharing }) },
    ),

  resolvedStep: (workflowId: string, stepId: string) =>
    request<{ resolved: ResolvedStep }>(
      `/workflows/${encodeURIComponent(workflowId)}/steps/${encodeURIComponent(stepId)}/resolved`,
    ),

  /**
   * Start a run, optionally with the work item it is about.
   *
   * Two fields rather than one, because they are two different things. The
   * message is the run's summary — what a person reading the run list sees.
   * The work item is the run's subject, and it is stored and read back by the
   * steps through a tool rather than retold down the handoff. Sending the item
   * as the message is what the studio used to do, and nothing ever parsed it.
   */
  runWorkflow: (id: string, message?: string, workItem?: unknown) =>
    request<{ runId: string; stepIds: string[] }>(`/workflows/${encodeURIComponent(id)}/run`, {
      method: 'POST',
      body: JSON.stringify({ message, ...(workItem === undefined ? {} : { workItem }) }),
    }),

  listRuns: (id: string) =>
    request<{ runs: RunSummary[] }>(`/workflows/${encodeURIComponent(id)}/runs`),

  getRun: (runId: string) =>
    request<{
      run: { id: string; workflowId: string; status: string };
      steps: StepRunSummary[];
    }>(`/runs/${encodeURIComponent(runId)}`),

  forkRun: (runId: string, body: { stepId: string; message?: string; scope?: 'step' | 'subtree' }) =>
    request<{ runId: string; stepIds: string[] }>(`/runs/${encodeURIComponent(runId)}/fork`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Ask a run that is already going to stop, pause, or carry on.
   *
   * The response carries `effect` rather than a bare acknowledgement, because
   * the three verbs do not land the same way: pausing takes hold immediately,
   * and cancelling waits for the current step to reach a point where stopping
   * leaves nothing half-written. Callers show what they are told rather than
   * asserting "cancelled" over a run that is still going.
   */
  controlRun: (runId: string, intent: 'cancel' | 'pause' | 'resume', reason?: string) =>
    request<{ runId: string; intent: string; effect: string }>(
      `/runs/${encodeURIComponent(runId)}/control`,
      { method: 'POST', body: JSON.stringify({ intent, reason }) },
    ),

  runIntent: (runId: string) =>
    request<{ intent: RunIntent | null }>(`/runs/${encodeURIComponent(runId)}/intent`),

  /**
   * The runs still in progress, on workflows the caller may read.
   *
   * `hidden` comes back alongside the list because the list is scoped: without
   * it, a board showing three goals where a colleague sees five looks like a
   * stale cache rather than like sharing doing its job.
   */
  goals: () => request<{ goals: Goal[]; count: number; hidden: number }>('/goals'),

  /**
   * Every recent run across the workflows the caller may read, newest first.
   *
   * The Runs landing page and Home's in-flight panel both read this, which is
   * what makes them agree: a run Home calls in flight is one the Runs screen
   * lists under the same filter. Scoped like `goals`, with the same `hidden`
   * alongside for the same reason.
   */
  listRecentRuns: (options: { status?: RunListStatus; windowHours?: number; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (options.status) query.set('status', options.status);
    if (options.windowHours !== undefined) query.set('windowHours', String(options.windowHours));
    if (options.limit !== undefined) query.set('limit', String(options.limit));
    const suffix = query.size > 0 ? `?${query}` : '';
    return request<RunListResponse>(`/runs${suffix}`);
  },

  listWebhookEndpoints: (workflowId: string) =>
    request<{ endpoints: WebhookEndpointSummary[] }>(
      `/workflows/${encodeURIComponent(workflowId)}/webhooks`,
    ),

  /** The only call that ever returns a token. Show it once, then it is gone. */
  createWebhookEndpoint: (workflowId: string, name?: string) =>
    request<{ endpoint: WebhookEndpointSummary; token: string; warning?: string }>(
      `/workflows/${encodeURIComponent(workflowId)}/webhooks`,
      { method: 'POST', body: JSON.stringify({ name }) },
    ),

  revokeWebhookEndpoint: (workflowId: string, endpointId: string) =>
    request<{ revoked: boolean }>(
      `/workflows/${encodeURIComponent(workflowId)}/webhooks/${encodeURIComponent(endpointId)}`,
      { method: 'DELETE' },
    ),

  /**
   * Teams to choose from in a Linear trigger.
   *
   * Answers with an empty list and a `reason` rather than failing when Linear
   * cannot be reached, because the field it feeds still accepts a typed key.
   */
  listLinearTeams: () => request<{ teams: LinearTeamSummary[]; reason?: string }>('/linear/teams'),

  /** Channels the Slack bot is in — the only ones a Slack trigger can fire in. */
  listSlackChannels: () =>
    request<{ channels: SlackChannelSummary[]; reason?: string }>('/slack/channels'),

  /**
   * The same list, from the settings surface, with the reason it is empty.
   *
   * A missing scope is the most common Slack misconfiguration and the most
   * invisible: `users.conversations` answers `missing_scope`, reef renders an
   * empty picker, and it reads as a workspace with no channels. The reason is
   * what turns that into something somebody can fix.
   */
  listSlackBotChannels: () =>
    request<{ channels: SlackChannelSummary[]; reason?: string }>('/slack/bot-channels'),

  /**
   * Every binding, plus this caller's own notification route.
   *
   * Pass a workflow id to narrow to the bindings that apply to it — its own,
   * and the workspace-wide ones it inherits.
   */
  slackSettings: (workflowId?: string) =>
    request<SlackSettings>(
      `/slack/bindings${workflowId ? `?workflowId=${encodeURIComponent(workflowId)}` : ''}`,
    ),

  bindSlackChannel: (body: {
    channelId: string;
    channelName?: string;
    /** Absent binds every workflow, which needs an admin. */
    workflowId?: string;
    purpose?: SlackChannelPurpose;
  }) =>
    request<{ id: string; channelId: string; workflowId: string | null; purpose: SlackChannelPurpose }>(
      '/slack/bindings',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  unbindSlackChannel: (channelId: string, workflowId?: string | null) =>
    request<{ removed: boolean }>(
      `/slack/bindings?channelId=${encodeURIComponent(channelId)}${
        workflowId ? `&workflowId=${encodeURIComponent(workflowId)}` : ''
      }`,
      { method: 'DELETE' },
    ),

  /** Yours and nobody else's — there is no endpoint that sets another person's. */
  setSlackReviewRoute: (reviewRoute: SlackReviewRoute) =>
    request<{ reviewRoute: SlackReviewRoute }>('/slack/preference', {
      method: 'PUT',
      body: JSON.stringify({ reviewRoute }),
    }),

  /** Yours, or everybody's if you are an admin. */
  listSlackIdentities: () =>
    request<{ identities: SlackIdentity[]; hidden: number }>('/slack/identities'),

  unlinkSlackIdentity: (externalId: string) =>
    request<{ unlinked: boolean }>(`/slack/identities/${encodeURIComponent(externalId)}`, {
      method: 'DELETE',
    }),

  /**
   * Who this workflow is shared with, as people rather than as ids.
   *
   * Labels are resolved server-side deliberately: doing it in the browser would
   * need an endpoint that maps ids to people in bulk, which is the enumeration
   * surface `/directory` exists to not offer.
   */
  workflowSharing: (workflowId: string) =>
    request<WorkflowSharing>(`/workflows/${encodeURIComponent(workflowId)}/sharing`),

  /** Groups a grant can name. Listed, unlike people. */
  listGroups: () => request<{ groups: GroupSummary[] }>('/directory/groups'),

  /**
   * Confirm one address you already have.
   *
   * A POST so the address stays out of access logs and browser history, and
   * exact-match so this is not a way to dump the staff list.
   */
  resolveUser: (email: string) =>
    request<{ userId: string; email: string; name: string | null }>('/directory/resolve-user', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  /**
   * Every share link that still opens something.
   *
   * A share token bypasses the session entirely, so this is an inventory of
   * standing credentials rather than a convenience.
   */
  outstandingShareLinks: () =>
    request<{ links: OutstandingShareLink[]; count: number; hidden: number }>(
      '/collaborate/share-links/outstanding',
    ),

  revokeShareLink: (id: string, workflowId: string) =>
    request<{ revoked: boolean }>(
      `/collaborate/share-links/${encodeURIComponent(id)}?workflowId=${encodeURIComponent(workflowId)}`,
      { method: 'DELETE' },
    ),

  listConnections: () => request<{ connections: ConnectionSummary[] }>('/connections'),

  listConnectors: () => request<{ connectors: ConnectorManifestSummary[] }>('/connectors'),

  createConnection: (body: {
    id: string;
    name: string;
    provider: string;
    configurator: Record<string, string>;
    /** Omitted by the studio: the API derives the manifest's suggested scope. */
    scope?: string[];
  }) =>
    request<{
      connection: ConnectionSummary;
      toolsDiscovered: number;
      note?: string;
      connectUrl?: string | null;
      discoveryError?: string;
    }>('/connections', { method: 'POST', body: JSON.stringify(body) }),

  updateConnectionScope: (id: string, scope: string[]) =>
    request<{ connection: ConnectionSummary }>(`/connections/${encodeURIComponent(id)}/scope`, {
      method: 'PUT',
      body: JSON.stringify({ scope }),
    }),

  /**
   * What a grant on this connection may name. Read-only, on the caller's own
   * credential, and answered by the API rather than assembled here — the
   * resource-URL rule has one correct copy and it is the one the scope check
   * uses.
   */
  connectionResources: (id: string, depth = 0, chosen: Record<string, string> = {}) => {
    const query = new URLSearchParams({ depth: String(depth) });
    for (const [key, value] of Object.entries(chosen)) query.set(key, value);
    return request<ConnectionResourcesResponse>(
      `/connections/${encodeURIComponent(id)}/resources?${query.toString()}`,
    );
  },

  refreshConnectionTools: (id: string) =>
    request<{
      tools: string[];
      driftSummary: string;
      needsApproval: boolean;
      drift: { added: string[]; removed: string[]; reshaped: string[] };
    }>(`/connections/${encodeURIComponent(id)}/refresh-tools`, { method: 'POST' }),

  approveCatalog: (id: string) =>
    request<{ approved: boolean; toolCount: number }>(
      `/connections/${encodeURIComponent(id)}/approve-catalog`,
      { method: 'POST' },
    ),

  revokeConnection: (id: string) =>
    request<{ revoked: boolean; revokedUpstream: boolean }>(
      `/connect/${encodeURIComponent(id)}/revoke`,
      { method: 'POST' },
    ),

  listActions: (query: { state?: string; connectionId?: string; runId?: string } = {}) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) if (value) search.set(key, value);
    return request<{ actions: StagedActionSummary[] }>(`/actions?${search.toString()}`);
  },

  applyAction: (id: string) =>
    request<{ applied: boolean; reason?: string }>(`/actions/${encodeURIComponent(id)}/apply`, {
      method: 'POST',
    }),

  rejectAction: (id: string, reason?: string) =>
    request<{ rejected: boolean }>(`/actions/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  revertAction: (id: string, note?: string) =>
    request<{ reverted: boolean }>(`/actions/${encodeURIComponent(id)}/revert`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  runCeilings: () =>
    request<{
      ceilings: RunCeilings;
      usage: { activeRuns: number; dailySpendCents: number };
      admitting: boolean;
      reason: string | null;
    }>('/run-ceilings'),

  saveRunCeilings: (ceilings: Partial<RunCeilings>) =>
    request<{ ceilings: RunCeilings }>('/run-ceilings', {
      method: 'PUT',
      body: JSON.stringify(ceilings),
    }),

  listSecrets: () => request<{ secrets: SecretSummary[] }>('/secrets'),

  putSecrets: (body: { scope?: string; scopeId?: string | null; key?: string; value?: string; env?: string }) =>
    request<{ stored: string[] }>('/secrets', { method: 'POST', body: JSON.stringify(body) }),

  deleteSecret: (scope: string, scopeId: string | null, key: string) => {
    const search = new URLSearchParams({ scope, key });
    if (scopeId) search.set('scopeId', scopeId);
    return request<{ deleted: boolean }>(`/secrets?${search.toString()}`, { method: 'DELETE' });
  },

  listPolicies: () => request<PolicyListResponse>('/policies'),

  editPolicy: (id: string, operations: unknown[], reason: string) =>
    request<PolicyEditResponse>(`/policies/${encodeURIComponent(id)}/edit`, {
      method: 'POST',
      body: JSON.stringify({ operations, reason }),
    }),

  createPolicy: (id: string, policy: Partial<PolicySpec>, reason: string) =>
    request<PolicySaveResponse>(`/policies/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ policy, reason }),
    }),

  deletePolicy: (id: string, reason: string) =>
    request<{ deleted: string; commit: string | null }>(`/policies/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),

  /**
   * The registry, in two shapes.
   *
   * `listSkills` is the name list the builder needs to validate a step's
   * `skills`, derived from the registry's own answer rather than from a second
   * endpoint — one address, one answer. `skillRegistry` is the whole thing:
   * every manifest with its lint findings, plus what has actually run.
   */
  listSkills: async () => ({
    skills: (await request<{ skills: SkillManifest[] }>('/skills')).skills.map(
      (skill) => skill.name,
    ),
  }),

  skillRegistry: (windowHours = 24 * 7) =>
    request<{
      windowHours: number;
      skills: SkillManifest[];
      usage: SkillUsage[];
      failing: number;
    }>(`/skills?windowHours=${windowHours}`),

  skill: (name: string) =>
    request<{
      skill: SkillManifest;
      instructions: string;
      usage: SkillUsage;
      proposals: Array<{ id: string; claim: string; state: string; createdAt: string }>;
    }>(`/skills/${encodeURIComponent(name)}`),

  /*
   * The software factory's three read surfaces. There is deliberately no write
   * here: an agent is its committed instructions, and changing one is a commit
   * to `skills/<name>/SKILL.md`, not a PUT.
   */
  factoryAgents: () =>
    request<{ agents: FactoryAgentSummary[]; workflowsPresent: string[] }>('/factory/agents'),

  /**
   * The factories — each lifecycle with what this deployment is doing in it —
   * and one of them opened to the prompts and access of every stage.
   */
  factories: () => request<{ factories: FactorySummary[] }>('/factory/factories'),

  factory: (id: string) =>
    request<{ factory: FactoryDetail }>(`/factory/factories/${encodeURIComponent(id)}`),

  /** The work items this person can see, placed on their factories. */
  workItems: (state?: string) =>
    request<WorkItemsResponse>(`/factory/work-items${state ? `?state=${encodeURIComponent(state)}` : ''}`),

  /** What the palette finds by a run id, a work item's title or a workflow's name. */
  search: (query: string) => request<SearchResponse>(`/search?q=${encodeURIComponent(query)}`),

  /** The repositories this person can name, for the run form's repo field. */
  factoryRepos: () => request<FactoryReposResponse>('/factory/repos'),

  factoryMetrics: (windowHours = 24) =>
    request<{ metrics: FactoryMetrics }>(`/factory/metrics?windowHours=${windowHours}`),

  /**
   * The platform underneath, in one read.
   *
   * One call rather than six, because the six numbers only make their argument
   * together: a page that let somebody conclude the graph is healthy while
   * nothing has leased a warm environment in a week is a page that hides the
   * thing they came for.
   */
  factoryPlatform: (windowHours = 24 * 7) =>
    request<{
      windowHours: number;
      platform: PlatformSummary;
      output: { agentAuthoredPullRequests: number; automatedMigrations: number };
    }>(`/factory/platform?windowHours=${windowHours}`),

  contextGraph: () =>
    request<{
      graph: {
        nodes: number;
        edges: number;
        byKind: Array<{ kind: string; count: number }>;
        bySource: Array<{ source: string; count: number }>;
      };
    }>('/context'),

  graphSearch: (query: string, kind?: string) =>
    request<{
      found: boolean;
      nodes?: GraphNodeRecord[];
      truncated?: boolean;
      note?: string;
    }>(`/context/search?q=${encodeURIComponent(query)}${kind ? `&kind=${encodeURIComponent(kind)}` : ''}`),

  graphNode: (id: string) =>
    request<{
      found: boolean;
      node?: GraphNodeRecord;
      related?: Array<{
        edge: string;
        direction: 'out' | 'in';
        node: GraphNodeRecord | { id: string; unresolved: true };
        weight: number;
      }>;
      truncated?: boolean;
      note?: string;
    }>(`/context/neighbours/${encodeURIComponent(id)}`),

  mcpGateway: () => request<{ gateway: GatewayStats }>('/gateway'),

  llmGateway: (windowHours = 24) =>
    request<{ gateway: LlmGatewayStats; guardBudgetMs: number; withinBudget: boolean }>(
      `/llm?windowHours=${windowHours}`,
    ),

  environments: (windowHours = 24) =>
    request<{
      windowHours: number;
      pools: EnvironmentPoolStats[];
      environments: WarmEnvironment[];
    }>(`/environments?windowHours=${windowHours}`),

  /**
   * Sessions, counted — the tiles.
   *
   * A day by default rather than a week: "done today" and "cancelled today" are
   * fixed to the UTC day whatever the window is, and pairing them with a
   * seven-day spend figure invites the two to be read as one number.
   */
  analytics: (windowHours = 24) =>
    request<AnalyticsSummary>(`/analytics?windowHours=${windowHours}`),

  /**
   * The token series behind the chart.
   *
   * Separate from `analytics` because the range buttons change this and nothing
   * else; re-counting every run in the deployment to redraw one line would make
   * the cheapest interaction on the page the most expensive request.
   */
  analyticsTokens: (window: TokenWindow = '7d') =>
    request<{ window: TokenWindow; days: number; series: TokenDay[] }>(
      `/analytics/tokens?window=${window}`,
    ),

  assistantProfiles: () =>
    request<{ profiles: AssistantProfile[]; stats: AssistantStats }>('/assistant'),

  /*
   * The operational controls.
   *
   * Grouped here rather than scattered among the reads they qualify, because
   * they are the four things somebody reaches for when the factory is doing
   * something they did not want — and at that moment the last thing they should
   * be doing is working out which screen owns which verb.
   */
  /**
   * The review queue: what has stopped and is waiting on a person.
   *
   * `hidden` is not decoration. Somebody seeing an empty queue while a
   * colleague sees three needs to know the difference is permission rather than
   * a fault, or this screen gets reported as broken.
   */
  reviewQueue: () =>
    request<{ items: ReviewItem[]; count: number; hidden: number }>('/review-queue'),

  /**
   * The three queues as one list, newest first. What the rail badge counts and
   * what Home shows; each item is decided through its own queue's endpoint.
   */
  inbox: () => request<InboxResponse>('/inbox'),

  /** Accept a claim: commits it into the skill. Admin-only, and the server says so. */
  acceptKnowledge: (id: string) =>
    request<{ accepted: boolean; commit: string; path: string }>(
      `/knowledge/${encodeURIComponent(id)}/accept`,
      { method: 'POST' },
    ),

  rejectKnowledge: (id: string, reason?: string) =>
    request<{ rejected: boolean }>(`/knowledge/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  approveReview: (id: string) =>
    request<{ decided: boolean; decision: string; runId: string; stepId: string }>(
      `/review-queue/${encodeURIComponent(id)}/approve`,
      { method: 'POST' },
    ),

  /** Send the plan back with a note on what to change. The step revises against it. */
  reviseReview: (id: string, feedback: string) =>
    request<{ decided: boolean; decision: string; runId: string; stepId: string }>(
      `/review-queue/${encodeURIComponent(id)}/revise`,
      { method: 'POST', body: JSON.stringify({ feedback }) },
    ),

  budgets: () =>
    request<{ windowHours: number; trailingDays: number; projects: ProjectSpend[] }>('/budgets'),

  setBudget: (workflowId: string, budget: Omit<ProjectBudget, 'workflowId' | 'updatedAt' | 'updatedBy'>) =>
    request<{ budget: ProjectBudget }>(`/budgets/${encodeURIComponent(workflowId)}`, {
      method: 'PUT',
      body: JSON.stringify({ workflowId, ...budget }),
    }),

  clearBudget: (workflowId: string) =>
    request<{ removed: boolean }>(`/budgets/${encodeURIComponent(workflowId)}`, {
      method: 'DELETE',
    }),

  projectPauses: () => request<{ pauses: ProjectPause[] }>('/budgets/pauses'),

  resumeProject: (workflowId: string) =>
    request<{ released: boolean }>(`/budgets/${encodeURIComponent(workflowId)}/resume`, {
      method: 'POST',
    }),

  stopSwitches: () =>
    request<{
      stopped: boolean;
      deploymentStopped: boolean;
      live: StopSwitch[];
      released: StopSwitch[];
    }>('/control/stop'),

  engageStop: (reason: string, scope: string | null) =>
    request<{ switch: StopSwitch }>('/control/stop', {
      method: 'POST',
      body: JSON.stringify({ reason, scope }),
    }),

  releaseStop: (id: string) =>
    request<{ released: boolean }>(`/control/stop/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  detections: (severity?: string) =>
    request<{ detections: DetectionHit[] }>(
      `/detections${severity ? `?severity=${encodeURIComponent(severity)}` : ''}`,
    ),

  acknowledgeDetection: (id: string) =>
    request<{ acknowledged: boolean }>(`/detections/${encodeURIComponent(id)}/acknowledge`, {
      method: 'POST',
    }),

  provenance: (query: { repo?: string; workflowId?: string; runId?: string } = {}) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) if (value) search.set(key, value);
    return request<{ provenance: ProvenanceRecord[] }>(`/provenance?${search.toString()}`);
  },

  skillEvals: () =>
    request<{
      summary: Array<{
        skill: string;
        cases: number;
        passed: number;
        failed: number;
        skipped: number;
        lastRanAt: string | null;
      }>;
      required: string[];
      /** Skills that govern a gate and have no case behind them. Should be empty. */
      uncovered: string[];
    }>('/skill-evals'),

  skillEvalDetail: (skill: string) =>
    request<{
      skill: string;
      summary: { skill: string; cases: number; passed: number; failed: number; skipped: number };
      cases: SkillEvalCase[];
      results: SkillEvalResult[];
      required: boolean;
    }>(`/skill-evals/${encodeURIComponent(skill)}`),

  ask: (question: string, profile = 'default') =>
    request<{ profile: string; answer: string }>('/assistant/ask', {
      method: 'POST',
      body: JSON.stringify({ question, profile, surface: 'web' }),
    }),

  audit: (query: Record<string, string | undefined>) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) if (value) search.set(key, value);
    return request<{ events: AuditEvent[] }>(`/audit?${search.toString()}`);
  },
};
